/**
 * Invoice schedules — FR-G6, T-3.5. Redrawn on the list shell in Wave 15D
 * (C14 §10.6 items 1–2, ADR-0033; T-6.16): search, status / client / cadence
 * chips, sortable columns, paging and Export; the new-retainer form moved to
 * `RetainerDialog`, where its two decisions are written down.
 *
 * A retainer is an arrangement, not a document: an amount, a cadence, and a
 * date it starts. The nightly run raises what each one owes at 02:00 IST, and
 * this screen is where a firm sets them up and sees what is coming.
 *
 * **"Run the due ones now" is safe to press twice**, and the result says so:
 * `already_raised` is a count, not an error. The same code the worker runs, so
 * a firm testing the button is testing the thing that runs at night.
 *
 * **Ending a schedule asks first** (ADR-0033 §1: anything that cancels asks):
 * this screen offers nothing on an ended schedule (it never has), so the only
 * way back from here is a new one. Pause and resume do not ask; each is undone
 * by the other.
 */

import { Alert, Button, ConfirmDialog } from '@fiveox/ui'
import { Play, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  statusTone,
  useListTable,
} from '../../components/page'
import { INVOICE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type BillingVocabulary,
  CADENCE_LABELS,
  ISSUE_MODE_LABELS,
  type Retainer,
  type RetainerRun,
  billing,
  rupees,
} from '../../lib/api/billing'
import { RetainerDialog } from './RetainerDialog'
import { Money, activeClients, clientFilter, namesOf } from './shared'

interface RetainerRow extends Retainer {
  client_name: string
}

export function Retainers() {
  const { can } = useEvaluation()
  const [retainers, setRetainers] = useState<Retainer[]>([])
  const [names, setNames] = useState<Record<string, string>>({})
  const [vocab, setVocab] = useState<BillingVocabulary | null>(null)
  const [creating, setCreating] = useState(false)
  const [run, setRun] = useState<RetainerRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [ending, setEnding] = useState<RetainerRow | null>(null)
  const [changing, setChanging] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [list, book, v] = await Promise.all([
        billing.retainers(),
        activeClients(),
        billing.vocabulary(),
      ])
      setRetainers(list.data)
      setNames(namesOf(book))
      setVocab(v.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The retainers could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const runNow = async () => {
    setBusy(true)
    setError(null)
    try {
      const resp = await billing.runRetainers()
      setRun(resp.data)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The run could not be started.')
    } finally {
      setBusy(false)
    }
  }

  const setStatus = async (row: Retainer, status: Retainer['status']) => {
    setError(null)
    setChanging(row.id)
    try {
      await billing.updateRetainer(row.id, { status })
      await load()
      return true
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That change could not be saved.')
      return false
    } finally {
      setChanging(null)
    }
  }

  const rows = useMemo<RetainerRow[]>(
    () => retainers.map((r) => ({ ...r, client_name: names[r.client_id] ?? '—' })),
    [retainers, names],
  )

  const columns = useMemo<ListColumn<RetainerRow>[]>(
    () => [
      {
        key: 'client',
        label: 'Client',
        width: 190,
        required: true,
        phone: true,
        sortField: 'client_name',
        render: (row) => (
          <span className="block min-w-0">
            <span
              className="block max-w-[11rem] truncate font-medium sm:max-w-[9rem]"
              title={row.client_name}
            >
              {row.client_name}
            </span>
            {/* On a phone the other columns are gone — the row's actions keep
                their place at the right edge — so the amount, the cadence and
                the status ride here. */}
            <span className="text-muted-foreground block text-xs sm:hidden">
              {rupees(row.amount)} · {CADENCE_LABELS[row.cadence]}
            </span>
            <span className="mt-1 block sm:hidden">
              <StatusBadge kind="retainer" status={row.status} />
            </span>
          </span>
        ),
        exportValue: (row) => row.client_name,
      },
      {
        key: 'description',
        label: 'What',
        width: 200,
        sortField: 'description',
        render: (row) => <span className="block max-w-[12rem] truncate">{row.description}</span>,
        exportValue: (row) => row.description,
      },
      {
        key: 'cadence',
        label: 'Every',
        width: 110,
        sortField: 'cadence',
        render: (row) => CADENCE_LABELS[row.cadence],
        exportValue: (row) => CADENCE_LABELS[row.cadence],
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 110,
        align: 'right',
        sortField: 'amount',
        render: (row) => <Money value={row.amount} />,
        exportValue: (row) => row.amount,
      },
      {
        key: 'next_run_on',
        label: 'Next',
        width: 110,
        sortField: 'next_run_on',
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">{row.next_run_on ?? '—'}</span>
        ),
        exportValue: (row) => row.next_run_on ?? '',
      },
      {
        key: 'issue_mode',
        label: 'At the run',
        width: 150,
        render: (row) => (
          <span className="text-muted-foreground block max-w-[9rem] text-xs whitespace-normal">
            {ISSUE_MODE_LABELS[row.issue_mode]}
          </span>
        ),
        exportValue: (row) => ISSUE_MODE_LABELS[row.issue_mode],
      },
      {
        key: 'status',
        label: 'Status',
        width: 100,
        sortField: 'status',
        render: (row) => <StatusBadge kind="retainer" status={row.status} />,
        exportValue: (row) => statusTone('retainer', row.status).label,
      },
    ],
    [],
  )

  // What the table draws, from the same columns the export carries (the
  // export keeps `columns`, its order and its seven columns). At 1280 beside
  // the rail the seven did not fit in front of the sticky actions and Status
  // sat under them, so the screen puts Status second, truncates the long
  // text with the whole of it in a tooltip, and folds *At the run* — not a
  // sort — under the cadence.
  const shown = useMemo<ListColumn<RetainerRow>[]>(() => {
    const pick = (key: string) => {
      const found = columns.find((c) => c.key === key)
      if (!found) throw new Error(`no retainer column ${key}`)
      return found
    }
    return [
      { ...pick('client'), width: 160 },
      pick('status'),
      {
        ...pick('description'),
        width: 150,
        render: (row) => (
          <span className="block max-w-[9rem] truncate" title={row.description}>
            {row.description}
          </span>
        ),
      },
      {
        ...pick('cadence'),
        width: 140,
        render: (row) => (
          <span className="block">
            {CADENCE_LABELS[row.cadence]}
            <span className="text-muted-foreground block max-w-[8rem] text-xs whitespace-normal">
              {ISSUE_MODE_LABELS[row.issue_mode]}
            </span>
          </span>
        ),
      },
      { ...pick('amount'), width: 100 },
      { ...pick('next_run_on'), width: 100 },
    ]
  }, [columns])

  const list = useListTable(rows, {
    columns: shown,
    search: (row) => [row.client_name, row.description],
    filters: [
      {
        key: 'status',
        label: 'Status',
        allLabel: 'Any status',
        options: (['active', 'paused', 'ended'] as const).map((status) => ({
          value: status,
          label: statusTone('retainer', status).label,
        })),
        match: (row, value) => row.status === value,
      },
      clientFilter(rows, names),
      {
        key: 'cadence',
        label: 'Every',
        allLabel: 'Any cadence',
        options: (['monthly', 'quarterly', 'half_yearly', 'annual'] as const).map((c) => ({
          value: c,
          label: CADENCE_LABELS[c],
        })),
        match: (row, value) => row.cadence === value,
      },
    ],
  })

  const canCreate = can(INVOICE, 'can_create')
  const canUpdate = can(INVOICE, 'can_update')
  const active = retainers.filter((r) => r.status === 'active').length

  const newRetainer = canCreate ? (
    <Button onClick={() => setCreating(true)}>
      <Plus size={16} aria-hidden /> New retainer
    </Button>
  ) : undefined

  return (
    <div className="space-y-6">
      <ListPage
        title="Retainers"
        description={`Invoice schedules. They run every night at 02:00; each period is raised once, whatever happens in between.${
          loading ? '' : ` ${active} active.`
        }`}
        primary={
          canUpdate || canCreate ? (
            <>
              {canUpdate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void runNow()}
                  loading={busy}
                  loadingText="Running…"
                >
                  <Play size={14} aria-hidden /> Run the due ones now
                </Button>
              )}
              {newRetainer}
            </>
          ) : undefined
        }
        loading={loading}
        error={error}
        isEmpty={list.isEmpty}
        empty={{
          title: 'No retainers yet',
          description:
            'A retainer bills the same client the same amount every month, quarter, half-year or year, without anybody remembering to.',
          action: newRetainer,
        }}
        toolbar={
          <ListToolbar
            list={list}
            placeholder="Search by client or what it is for"
            actions={
              <ExportButton rows={list.sorted} columns={columns} filename="prodesk-retainers" />
            }
          />
        }
      >
        {run && (
          <Alert tone={run.failed > 0 ? 'warning' : 'success'}>
            {run.due === 0
              ? 'Nothing was due.'
              : `${run.due} period${run.due === 1 ? '' : 's'} due — ${run.drafted} drafted, ${run.issued} issued, ${run.already_raised} already raised.`}
            {run.failed > 0 && ` ${run.failed} could not be raised — check the schedules below.`}
            {run.outstanding > 0 &&
              ` ${run.outstanding} schedule${run.outstanding === 1 ? ' has' : 's have'} more periods owed than one pass raises; run it again.`}
          </Alert>
        )}
        <ListTable
          list={list}
          rowKey={(row) => row.id}
          trailing={
            canUpdate
              ? {
                  header: <span className="sr-only">Actions</span>,
                  width: 150,
                  cell: (row) =>
                    row.status === 'ended' ? null : (
                      <span className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={changing === row.id}
                          onClick={() =>
                            void setStatus(row, row.status === 'active' ? 'paused' : 'active')
                          }
                        >
                          {row.status === 'active' ? 'Pause' : 'Resume'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={changing === row.id}
                          onClick={() => setEnding(row)}
                        >
                          End
                        </Button>
                      </span>
                    ),
                }
              : undefined
          }
        />
      </ListPage>


      {creating && (
        <RetainerDialog
          vocab={vocab}
          book={names}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void load()
          }}
        />
      )}

      <ConfirmDialog
        open={ending !== null}
        onClose={() => setEnding(null)}
        title={ending ? `End the retainer for ${ending.client_name}?` : 'End this retainer?'}
        confirmLabel="End it"
        tone="danger"
        busy={ending !== null && changing === ending.id}
        onConfirm={() => {
          if (!ending) return
          void setStatus(ending, 'ended').then((ok) => {
            if (ok) setEnding(null)
          })
        }}
      >
        <p className="text-muted-foreground text-sm">
          Nothing more is raised by it. The invoices it has already raised stay as they are. This
          screen cannot resume an ended schedule — to bill this client again, start a new
          retainer.
        </p>
      </ConfirmDialog>
    </div>
  )
}
